'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Storefront } from '@phosphor-icons/react'
import { useGetProducersNearMeQuery } from 'graphql/generated/graphql'
import { getUserLocation } from 'hook/listings/useFetchListings'
import { shuffleEnhancedListings } from '@/lib/utils'
import { AddToMealPlanner } from '@/components/meal-planner/AddToMealPlanner'
import type { PlannerRecipeDraft } from '@/lib/mealPlanner/types'

const VISIBLE_COUNT = 3
const ROTATE_MS = 5000
const DEFAULT_RADIUS = 10

function formatProducerProducts(ingredients?: Array<{ name?: string | null } | null> | null) {
  if (!ingredients?.length) return ''
  const names = ingredients
    .map((item) => item?.name)
    .filter(Boolean)
    .slice(0, 2) as string[]
  if (!names.length) return ''
  const label = names.join(', ')
  return ingredients.length > 2 ? `${label}...` : label
}

export function RecipeRightRail({ plannerRecipe }: { plannerRecipe?: PlannerRecipeDraft | null }) {
  const router = useRouter()
  const [locationLoading, setLocationLoading] = useState(true)
  const [coords, setCoords] = useState<{ latitude: number; longitude: number }>({
    latitude: 0,
    longitude: 0,
  })
  const [searchRadius, setSearchRadius] = useState(DEFAULT_RADIUS)
  const [showEmpty, setShowEmpty] = useState(false)
  const [startIndex, setStartIndex] = useState(0)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        setLocationLoading(true)
        const userLocation = await getUserLocation()
        if (
          !cancelled &&
          userLocation.latitude !== 0 &&
          userLocation.longitude !== 0
        ) {
          setCoords({
            latitude: userLocation.latitude,
            longitude: userLocation.longitude,
          })
        }
      } finally {
        if (!cancelled) setLocationLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const { data, loading } = useGetProducersNearMeQuery({
    skip: locationLoading || !coords.latitude || !coords.longitude,
    variables: {
      lat: coords.latitude,
      long: coords.longitude,
      distance: searchRadius,
      limit: 40,
      offset: 0,
    },
    fetchPolicy: 'no-cache',
  })

  useEffect(() => {
    if (loading || locationLoading) return
    const results = data?.getProducersNearMe || []
    if (results.length > 0) {
      setShowEmpty(false)
      return
    }
    if (searchRadius === DEFAULT_RADIUS) {
      setSearchRadius(25)
    } else if (searchRadius === 25) {
      setSearchRadius(75)
    } else {
      setShowEmpty(true)
    }
  }, [data, loading, locationLoading, searchRadius])

  const shuffledProducers = useMemo(
    () => shuffleEnhancedListings(data?.getProducersNearMe?.slice() || []),
    [data]
  )

  useEffect(() => {
    setStartIndex(0)
  }, [shuffledProducers])

  useEffect(() => {
    if (shuffledProducers.length <= VISIBLE_COUNT) return
    const timer = setInterval(() => {
      setStartIndex((prev) => (prev + 1) % shuffledProducers.length)
    }, ROTATE_MS)
    return () => clearInterval(timer)
  }, [shuffledProducers.length])

  const visibleProducers = useMemo(() => {
    if (shuffledProducers.length <= VISIBLE_COUNT) return shuffledProducers
    return Array.from({ length: VISIBLE_COUNT }, (_, index) => {
      return shuffledProducers[(startIndex + index) % shuffledProducers.length]
    })
  }, [shuffledProducers, startIndex])

  const producersLoading = locationLoading || loading

  return (
    <aside className="flex w-full flex-col gap-4 xl:w-[309px] xl:shrink-0">
      {plannerRecipe && <AddToMealPlanner recipe={plannerRecipe} />}
      <div className="flex flex-col items-center gap-3 rounded-2xl bg-[#F3FAEB] px-4 pb-4 pt-5">
        <Image
          src="/assets/icons/salad-bowl.png"
          alt=""
          width={125}
          height={125}
          className="h-[125px] w-[125px] object-contain"
        />
        <div className="flex w-full flex-col gap-1.5">
          <h3 className="text-center text-xl font-semibold leading-7 text-[#202020]">
            More to Discover
          </h3>
          <p className="text-center text-base leading-6 text-[#333333]">
            Find new recipes, discover local food, connect with the community,
            and explore what&apos;s around you.
          </p>
        </div>
        <button
          type="button"
          onClick={() => router.push('/discover')}
          className="flex h-10 w-full items-center justify-center rounded-full bg-[#85C349] text-base font-semibold tracking-tight text-white hover:bg-[#6EAC36]"
        >
          Explore More
        </button>
      </div>

      <div className="flex flex-col gap-4 rounded-2xl bg-[#F3FAEB] p-4">
        <div className="flex items-center gap-2">
          <Storefront size={20} color="#2E7D0F" weight="regular" />
          <h3 className="flex-1 text-lg font-semibold leading-[22px] text-[#2E7D0F]">
            Shop local farms near you
          </h3>
        </div>
        <p className="text-sm leading-5 text-[#6B6B6B]">
          Find great tasting, fresh ingredients in your area.
        </p>

        {producersLoading && (
          <div className="flex flex-col gap-2">
            {Array.from({ length: VISIBLE_COUNT }).map((_, index) => (
              <div
                key={index}
                className="h-[88px] animate-pulse rounded-xl border border-[#E6E6E6] bg-white"
              />
            ))}
          </div>
        )}

        {!producersLoading && showEmpty && (
          <p className="text-sm leading-5 text-[#6B6B6B]">
            No local farms found nearby yet. Check back soon or browse all
            producers.
          </p>
        )}

        {!producersLoading && !showEmpty && (
          <div className="flex flex-col gap-2">
            {visibleProducers.map((producer) => {
              const products = formatProducerProducts(producer.ingredients)
              const distance =
                producer.distance != null && !Number.isNaN(Number(producer.distance))
                  ? `${Math.round(Number(producer.distance))} mi`
                  : null

              return (
                <Link
                  key={producer.listing_id}
                  href={`/producer/${producer.slug}`}
                  className="flex flex-col gap-2 rounded-xl border border-[#E6E6E6] bg-white p-3 transition-colors hover:bg-[#FAFAFA]"
                >
                  <div className="flex items-start gap-3">
                    <Image
                      src={producer.photo || '/assets/icons/tractor.svg'}
                      alt=""
                      width={32}
                      height={32}
                      className="h-8 w-8 shrink-0 rounded-full object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold leading-[19px] text-[#202020]">
                          {producer.name}
                        </span>
                        {distance && (
                          <span className="text-[13px] leading-4 text-[#6B6B6B]">
                            • {distance}
                          </span>
                        )}
                      </div>
                      {products && (
                        <p className="mt-1 text-sm leading-[17px] text-[#333333]">
                          {products}
                        </p>
                      )}
                      {producer.city && producer.state && (
                        <p className="mt-1 text-xs leading-4 text-[#6B6B6B]">
                          {producer.city}, {producer.state}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        )}

        <button
          type="button"
          onClick={() => router.push('/display?type=producers')}
          className="text-left text-base font-semibold leading-[19px] text-[#2E7D0F] hover:underline"
        >
          Expand to view all →
        </button>
      </div>

      {/* Ad slot — hidden until ad inventory is available
      <div className="flex min-h-[200px] items-center justify-center rounded-2xl bg-[rgba(230,230,230,0.3)] px-4 py-16">
        <p className="text-center text-base leading-6 text-[#333333]">
          Space for Ads
        </p>
      </div>
      */}
    </aside>
  )
}
